import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Search, CornerDownLeft, ExternalLink, FolderKanban, User, type LucideIcon } from 'lucide-react';
import { useAuth } from '@/auth/useAuth';
import { useGuideOptional } from '@/features/guide/useGuide';
import { api } from '@/api/client';
import { cn } from '@/lib/utils';
import { type NavItem } from './navItems';
import { visibleNavItems } from './navWorkspaces';

/**
 * Jump to anything — Ctrl/⌘K (2026-08-27, extended to records 2026-08-31).
 *
 * The point of the palette is that it removes the need to navigate at all:
 * type three letters and press Enter. It matters most for the people with the
 * most permissions, whose nav is widest and who move between unrelated corners
 * of the app all day.
 *
 * PAGES come from the same route-to-workspace configuration the rail renders
 * from, filtered by the same permission gates, so the palette can never offer
 * a page the nav would hide or a link that is guaranteed to 403. Each entry
 * also carries `keywords` — what people actually call the page — so "hire"
 * finds Equipment and "bid" finds Leads without either word in the label.
 *
 * PROJECTS and PEOPLE come from the list endpoints those pages already use
 * (/projects?search=, /employees?search=) — no new backend search system, per
 * Michael. They are gated on the same read permissions the endpoints enforce,
 * debounced, and capped at five each so the pages stay visible above them.
 *
 * Every record lookup degrades to nothing: no permission, a failed request,
 * offline, or a body that is not the shape we expect all produce an empty
 * section, and the palette still jumps to pages. A nav that cannot navigate
 * because a search endpoint is down would be a worse bug than the one it is
 * searching around.
 */

/** Below this a search matches half the company and costs a round trip. */
const MIN_QUERY = 2;
const DEBOUNCE_MS = 200;
const MAX_RECORDS = 5;

interface PaletteResult {
  key: string;
  to: string;
  icon: LucideIcon;
  label: string;
  /** Right-aligned context — a job number, a trade. Never the only identifier. */
  hint?: string;
  /**
   * What Enter does when the result is not an in-app route: 'external' leaves
   * for `to`, 'guide' opens the Guide. Absent means navigate(), the normal
   * case. Records are always routes, so only nav entries ever set it.
   */
  kind?: 'external' | 'guide';
}

function useDebounced<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

/** A field the API may legitimately send as null — never rendered raw. */
function text(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

/**
 * Both endpoints answer { data: [...], meta: {...} } (successWithMeta). The
 * guard is not defensiveness for its own sake: this hook is called from tests
 * and pages that mock `api.get` wholesale, and rendering whatever came back
 * would be exactly the React-error-#31 class of bug that put an object where a
 * string was expected.
 */
function rows(body: unknown): Record<string, unknown>[] {
  const data = (body as { data?: unknown } | undefined)?.data;
  if (!Array.isArray(data)) return [];
  return data.filter((row): row is Record<string, unknown> => !!row && typeof row === 'object');
}

function useProjectResults(term: string, enabled: boolean) {
  return useQuery({
    queryKey: ['palette', 'projects', term],
    enabled,
    // One failed lookup is enough; the palette must not sit retrying while
    // someone is trying to get somewhere.
    retry: false,
    staleTime: 30_000,
    queryFn: async (): Promise<PaletteResult[]> => {
      const body = await api.get<unknown>(
        `/api/v1/projects?search=${encodeURIComponent(term)}&per_page=${MAX_RECORDS}`,
      );
      return rows(body)
        .map((row): PaletteResult | undefined => {
          const id = text(row.id);
          const name = text(row.name);
          const number = text(row.project_number);
          if (!id) return undefined;
          return {
            key: `project-${id}`,
            to: `/projects/${id}`,
            icon: FolderKanban,
            label: name || number || 'Untitled job',
            hint: name ? number || undefined : undefined,
          };
        })
        .filter((r): r is PaletteResult => !!r)
        .slice(0, MAX_RECORDS);
    },
  });
}

function useEmployeeResults(term: string, enabled: boolean) {
  return useQuery({
    queryKey: ['palette', 'employees', term],
    enabled,
    retry: false,
    staleTime: 30_000,
    queryFn: async (): Promise<PaletteResult[]> => {
      const body = await api.get<unknown>(
        `/api/v1/employees?search=${encodeURIComponent(term)}&per_page=${MAX_RECORDS}`,
      );
      return rows(body)
        .map((row): PaletteResult | undefined => {
          const id = text(row.id);
          if (!id) return undefined;
          // display_name is a nullable column, so build the fallback rather
          // than printing an empty row.
          const name = text(row.display_name)
            || `${text(row.first_name)} ${text(row.last_name)}`.trim()
            || text(row.employee_number);
          return {
            key: `employee-${id}`,
            to: `/employees/${id}`,
            icon: User,
            label: name || 'Employee',
            hint: text(row.trade) || undefined,
          };
        })
        .filter((r): r is PaletteResult => !!r)
        .slice(0, MAX_RECORDS);
    },
  });
}

function score(item: NavItem, q: string): number {
  const label = item.label.toLowerCase();
  const keywords = (item.keywords ?? '').toLowerCase();

  // Ranked so the thing you almost certainly meant sorts first: an exact label,
  // then a label that starts with what you typed, then a label containing it,
  // and only then a keyword match.
  if (label === q) return 0;
  if (label.startsWith(q)) return 1;
  if (label.includes(q)) return 2;
  if (keywords.includes(q)) return 3;
  return -1;
}

export function CommandPalette({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { can, user } = useAuth();
  const navigate = useNavigate();
  // Optional: these four surfaces are rendered inside GuideProvider in the
  // app, but each is unit-tested on its own — the optional hook returns null
  // instead of throwing, so the nav never depends on the Guide being mounted.
  const guide = useGuideOptional();
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  // Operator experience (2026-09-17): the palette's corpus is the same
  // filtered configuration the rail shows — My job alone for an operator.
  const experience = user?.experience;
  const all = useMemo(() => visibleNavItems(can, experience), [can, experience]);

  const trimmed = query.trim();
  const debounced = useDebounced(trimmed, DEBOUNCE_MS);
  const searchable = open && debounced.length >= MIN_QUERY;

  // Gated on the exact permissions the endpoints themselves enforce, so the
  // request is never made by someone who would only get a 403 back.
  // An operator holds neither permission (the server intersects), so the
  // record lookups are already off for them; the explicit flag keeps it so
  // even for a cached session that predates the wall.
  const operator = experience === 'operator';
  const projects = useProjectResults(debounced, searchable && !operator && can('projects.view'));
  const employees = useEmployeeResults(debounced, searchable && !operator && can('employees.view'));

  const pages = useMemo<PaletteResult[]>(() => {
    const q = trimmed.toLowerCase();
    const matched = q
      ? all
        .map((item) => ({ item, rank: score(item, q) }))
        .filter((r) => r.rank >= 0)
        .sort((a, b) => a.rank - b.rank)
        .map((r) => r.item)
      : all;
    return matched.map((item) => ({
      key: `page-${item.to}`,
      to: item.to,
      icon: item.icon,
      label: item.label,
      kind: item.external ? 'external' as const : item.action === 'guide' ? 'guide' as const : undefined,
    }));
  }, [all, trimmed]);

  const sections = useMemo(() => ([
    { key: 'pages', label: 'Pages', results: pages },
    { key: 'projects', label: 'Projects', results: projects.data ?? [] },
    { key: 'employees', label: 'People', results: employees.data ?? [] },
  ].filter((s) => s.results.length > 0)), [pages, projects.data, employees.data]);

  // One flat list behind the sections so ArrowDown walks from the last page
  // into the first project without the person knowing there are sections.
  const flat = useMemo(() => sections.flatMap((s) => s.results), [sections]);
  // Where each section starts in that flat list — the highlight and Enter run
  // off one index, and computing it during render would mutate as React walks.
  const offsets = useMemo(() => {
    let next = 0;
    return sections.map((s) => { const start = next; next += s.results.length; return start; });
  }, [sections]);

  useEffect(() => { setActive(0); }, [query]);
  // Records arrive after the pages do; an index that pointed past the end
  // would make Enter do nothing.
  useEffect(() => { setActive((a) => Math.min(a, Math.max(flat.length - 1, 0))); }, [flat.length]);

  useEffect(() => {
    if (open) {
      setQuery('');
      // Focus after paint so the caret lands in the field rather than nowhere.
      const id = window.setTimeout(() => inputRef.current?.focus(), 0);
      return () => window.clearTimeout(id);
    }
    return undefined;
  }, [open]);

  if (!open) return null;

  function go(result: PaletteResult | undefined) {
    if (!result) return;
    // Never hand a portal URL or a '#' id to navigate() — react-router would
    // push it as a path and land the person on DemoOps' 404.
    if (result.kind === 'external') window.location.assign(result.to);
    else if (result.kind === 'guide') guide?.setOpen(true);
    else navigate(result.to);
    onClose();
  }

  return (
    // z-[80]: modal, so over the Guide (z-[70]) and the top row's open menus
    // (the bell's and NADC apps', z-[75]) — at z-[60] Ctrl+K opened it under
    // an open menu (compact chrome review 4, 2026-10-11).
    <div
      className="fixed inset-0 z-[80] flex items-start justify-center bg-black/60 p-4 pt-[12vh]"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Jump to a page"
        className="w-full max-w-lg overflow-hidden rounded-xl border border-graphite-700 bg-graphite-900 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 border-b border-graphite-800 px-3">
          <Search className="h-4 w-4 shrink-0 text-graphite-500" aria-hidden />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(a + 1, flat.length - 1)); }
              else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
              else if (e.key === 'Enter') { e.preventDefault(); go(flat[active]); }
              else if (e.key === 'Escape') { e.preventDefault(); onClose(); }
            }}
            placeholder="Jump to a page, job, or person…"
            // Distinct from the dialog's own label: sharing one makes "Jump to
            // a page" ambiguous between the container and the field inside it.
            aria-label="Search pages"
            aria-controls="command-palette-results"
            className="min-h-touch w-full bg-transparent py-3 text-sm text-graphite-100 outline-none placeholder:text-graphite-500"
          />
          <kbd className="shrink-0 rounded border border-graphite-700 px-1.5 py-0.5 text-[10px] text-graphite-500">esc</kbd>
        </div>

        {flat.length === 0 ? (
          <p className="px-4 py-6 text-center text-sm text-graphite-500">
            {/* Name what was searched — a bare "no results" tells you nothing. */}
            Nothing matches “{query}”. Try the name of a page, like “invoices”.
          </p>
        ) : (
          <div id="command-palette-results" className="max-h-80 overflow-y-auto py-1">
            {sections.map((section, si) => (
              <div key={section.key}>
                {/* The heading appears only once records join the pages —
                    with nothing but pages there is nothing to tell apart. */}
                {sections.length > 1 && (
                  <p className="px-4 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wider text-graphite-600">
                    {section.label}
                  </p>
                )}
                <ul>
                  {section.results.map((result, ri) => {
                    const i = offsets[si] + ri;
                    const Icon = result.icon;
                    return (
                      <li key={result.key}>
                        <button
                          type="button"
                          onMouseEnter={() => setActive(i)}
                          onClick={() => go(result)}
                          className={cn(
                            'flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm',
                            i === active ? 'bg-safety-500/10 text-safety-400' : 'text-graphite-300',
                          )}
                        >
                          <Icon className="h-4 w-4 shrink-0" aria-hidden />
                          <span className="flex-1 truncate">{result.label}</span>
                          {result.kind === 'external' && (
                            <ExternalLink className="h-3.5 w-3.5 shrink-0 text-graphite-500" aria-hidden />
                          )}
                          {result.hint && (
                            <span className="shrink-0 text-xs text-graphite-500">{result.hint}</span>
                          )}
                          {i === active && <CornerDownLeft className="h-3.5 w-3.5 shrink-0 text-graphite-500" aria-hidden />}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default CommandPalette;
